import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripeTerminal } from '@stripe/terminal-js';
import { api } from '../api/client';
import { STRIPE_PUBLISHABLE_KEY } from '../config';
import './PaymentModal.css';

// Card Input Form Component
function CardPaymentForm({ amount, invoiceId, stripeAccountId, onSuccess, onError, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [cardElement, setCardElement] = useState(null);

  // Initialize Stripe with connected account on component mount
  useEffect(() => {
    const initStripe = async () => {
      if (!stripeAccountId) {
        console.log('Waiting for stripe account ID...');
        return;
      }
      
      console.log('Initializing Stripe with connected account:', stripeAccountId);
      
      // Initialize Stripe with the connected account from the start
      const stripeInstance = await loadStripe(
        STRIPE_PUBLISHABLE_KEY,
        {
          stripeAccount: stripeAccountId  // Critical: initialize WITH connected account
        }
      );
      
      setStripe(stripeInstance);
      
      if (stripeInstance) {
        const elementsInstance = stripeInstance.elements();
        
        const card = elementsInstance.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#32325d',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#fa755a',
            },
          },
        });
        card.mount('#card-element');
        
        // Auto-focus the card input after mounting
        setTimeout(() => {
          card.focus();
        }, 100);
        
        setCardElement(card);
      }
    };
    
    initStripe();
    
    return () => {
      if (cardElement) {
        cardElement.destroy();
      }
    };
  }, [stripeAccountId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !cardElement) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Create Payment Intent on backend
      const { data } = await api.post('/stripe/create-payment-intent', {
        amount: amount,
        currency: 'usd',
        invoice_id: invoiceId
      });

      const { clientSecret, paymentIntentId } = data;
      
      console.log('Confirming payment with client secret:', clientSecret);

      // Step 2: Confirm payment using the same Stripe instance that created the card element
      // (This instance was already initialized with the connected account)
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (result.error) {
        setError(result.error.message);
        onError(result.error.message);
      } else {
        console.log('Payment succeeded! Payment Intent ID:', result.paymentIntent.id);
        
        // Step 3: Return success to parent — GHL invoice creation + recording
        // happens AFTER Stripe succeeds (in handlePaymentModalClose)
        // This ensures the GHL invoice is always fresh and has the correct amountDue
        onSuccess({
          paymentIntentId: result.paymentIntent.id,
          status: result.paymentIntent.status,
          amount: amount
        });
      }
    } catch (err) {
      let errorMessage = 'Payment failed';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(d => `${d.loc?.slice(-1)[0] || 'Field'}: ${d.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card-payment-form">
      <div className="card-element-container">
        <div id="card-element"></div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="modal-actions">
        <button 
          type="button" 
          onClick={onCancel} 
          className="btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn-primary"
          disabled={!stripe || loading}
        >
          {loading ? 'Processing...' : `Charge $${amount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
}

// Reader Payment Component
function ReaderPayment({ amount, invoiceId, onSuccess, onError, onCancel }) {
  const [terminal, setTerminal] = useState(null);
  const [readers, setReaders] = useState([]);
  const [selectedReader, setSelectedReader] = useState(null);
  const [connectedReader, setConnectedReader] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeTerminal();
  }, []);

  const initializeTerminal = async () => {
    try {
      const StripeTerminal = await loadStripeTerminal();
      
      const terminalInstance = StripeTerminal.create({
        onFetchConnectionToken: async () => {
          const { data } = await api.post('/stripe/connection-token');
          return data.secret;
        },
        onUnexpectedReaderDisconnect: () => {
          setConnectedReader(null);
          setStatus('Reader disconnected unexpectedly');
        },
      });

      setTerminal(terminalInstance);
      setStatus('Terminal initialized');
    } catch (err) {
      setError('Failed to initialize terminal: ' + err.message);
    }
  };

  const discoverReaders = async () => {
    if (!terminal) return;

    setLoading(true);
    setStatus('Discovering readers...');
    setError(null);

    try {
      const discoverResult = await terminal.discoverReaders({
        simulated: false,
      });

      if (discoverResult.error) {
        throw new Error(discoverResult.error.message);
      }

      setReaders(discoverResult.discoveredReaders);
      
      if (discoverResult.discoveredReaders.length === 0) {
        setStatus('No readers found. Make sure your reader is powered on.');
      } else {
        setStatus(`Found ${discoverResult.discoveredReaders.length} reader(s)`);
      }
    } catch (err) {
      setError('Failed to discover readers: ' + err.message);
      setStatus('');
    } finally {
      setLoading(false);
    }
  };

  const connectReader = async (reader) => {
    if (!terminal) return;

    setLoading(true);
    setStatus('Connecting to reader...');
    setError(null);

    try {
      const connectResult = await terminal.connectReader(reader);

      if (connectResult.error) {
        throw new Error(connectResult.error.message);
      }

      setConnectedReader(connectResult.reader);
      setStatus(`Connected to ${connectResult.reader.label || 'Reader'}`);
    } catch (err) {
      setError('Failed to connect to reader: ' + err.message);
      setStatus('');
    } finally {
      setLoading(false);
    }
  };

  const collectPayment = async () => {
    if (!terminal || !connectedReader) return;

    setLoading(true);
    setStatus('Creating payment intent...');
    setError(null);

    try {
      // Step 1: Create Payment Intent
      const { data } = await api.post('/stripe/create-payment-intent', {
        amount: amount,
        currency: 'usd',
        invoice_id: invoiceId,
        is_terminal: true
      });

      const { clientSecret, paymentIntentId } = data;

      // Step 2: Collect payment method from reader
      setStatus('Present card to reader...');
      const collectResult = await terminal.collectPaymentMethod(clientSecret);

      if (collectResult.error) {
        throw new Error(collectResult.error.message);
      }

      // Step 3: Process payment
      setStatus('Processing payment...');
      const processResult = await terminal.processPayment(collectResult.paymentIntent);

      if (processResult.error) {
        throw new Error(processResult.error.message);
      }

      // Step 4: Get card details if available
      let cardBrand = 'Card';
      let cardLast4 = '****';
      
      if (processResult.paymentIntent.charges && processResult.paymentIntent.charges.data.length > 0) {
        const charge = processResult.paymentIntent.charges.data[0];
        if (charge.payment_method_details && charge.payment_method_details.card_present) {
          cardBrand = charge.payment_method_details.card_present.brand || 'Card';
          cardLast4 = charge.payment_method_details.card_present.last4 || '****';
        }
      }

      // Step 5: Record payment in GHL
      setStatus('Recording payment...');
      await api.post('/stripe/record-payment', {
        invoice_id: invoiceId,
        payment_intent_id: paymentIntentId,
        amount: amount,
        card_brand: cardBrand,
        card_last4: cardLast4
      });

      onSuccess({
        paymentIntentId: processResult.paymentIntent.id,
        status: processResult.paymentIntent.status
      });

    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Payment failed';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reader-payment">
      {!connectedReader ? (
        <>
          <button 
            onClick={discoverReaders} 
            className="btn-primary"
            disabled={loading || !terminal}
          >
            {loading ? 'Discovering...' : 'Discover Readers'}
          </button>

          {readers.length > 0 && (
            <div className="readers-list">
              <h4>Available Readers:</h4>
              {readers.map((reader) => (
                <div key={reader.id} className="reader-item">
                  <div>
                    <strong>{reader.label || 'Unnamed Reader'}</strong>
                    <p className="reader-serial">{reader.serial_number}</p>
                  </div>
                  <button
                    onClick={() => connectReader(reader)}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Connect
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="connected-reader">
          <div className="reader-status">
            <span className="status-indicator connected"></span>
            <strong>Connected: {connectedReader.label || 'Reader'}</strong>
          </div>
          
          <button
            onClick={collectPayment}
            className="btn-primary btn-large"
            disabled={loading}
          >
            {loading ? status : `Collect Payment ($${amount.toFixed(2)})`}
          </button>
        </div>
      )}

      {status && <div className="status-message">{status}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="modal-actions">
        <button 
          onClick={onCancel} 
          className="btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Main Payment Modal Component
export default function PaymentModal({ isOpen, onClose, amount, invoiceId }) {
  // Respect card payment mode from settings
  const getInitialPaymentMethod = () => {
    const cardPaymentMode = localStorage.getItem('card_payment_mode') || 'reader_first';
    return cardPaymentMode === 'manual_entry' ? 'manual' : 'reader';
  };
  
  const [paymentMethod, setPaymentMethod] = useState(getInitialPaymentMethod());
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(true);

  // Fetch the Stripe connected account ID when modal opens
  useEffect(() => {
    const fetchStripeAccount = async () => {
      if (!isOpen) return;
      
      // Reset payment method based on current settings when modal opens
      const cardPaymentMode = localStorage.getItem('card_payment_mode') || 'reader_first';
      setPaymentMethod(cardPaymentMode === 'manual_entry' ? 'manual' : 'reader');
      
      try {
        setLoadingAccount(true);
        const { data } = await api.get('/stripe/status');
        if (data.connected && data.account_id) {
          console.log('Using Stripe account:', data.account_id);
          setStripeAccountId(data.account_id);
        }
      } catch (error) {
        console.error('Failed to get Stripe account:', error);
      } finally {
        setLoadingAccount(false);
      }
    };
    
    fetchStripeAccount();
  }, [isOpen]);

  const handleSuccess = (result) => {
    setPaymentResult(result);
    setPaymentComplete(true);
    
    // Close modal and notify parent after 2 seconds
    setTimeout(() => {
      onClose(true);
    }, 2000);
  };

  const handleError = (error) => {
    console.error('Payment error:', error);
  };

  const handleCancel = () => {
    onClose(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content payment-modal">
        {paymentComplete ? (
          <div className="payment-success">
            <div className="success-icon">✓</div>
            <h2>Payment Successful!</h2>
            <p>Amount: ${amount.toFixed(2)}</p>
            <p className="success-message">Invoice has been marked as paid</p>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h2>Process Payment</h2>
              <button onClick={handleCancel} className="close-btn">×</button>
            </div>

            <div className="payment-amount">
              <label>Total Amount:</label>
              <strong>${amount.toFixed(2)}</strong>
            </div>

            <div className="payment-method-tabs">
              <button
                className={paymentMethod === 'reader' ? 'active' : ''}
                onClick={() => setPaymentMethod('reader')}
              >
                Use Reader
              </button>
              <button
                className={paymentMethod === 'manual' ? 'active' : ''}
                onClick={() => setPaymentMethod('manual')}
              >
                Manual Entry
              </button>
            </div>

            <div className="payment-method-content">
              {loadingAccount ? (
                <div className="status-message">Loading payment form...</div>
              ) : paymentMethod === 'manual' ? (
                <CardPaymentForm
                  amount={amount}
                  invoiceId={invoiceId}
                  stripeAccountId={stripeAccountId}
                  onSuccess={handleSuccess}
                  onError={handleError}
                  onCancel={handleCancel}
                />
              ) : (
                <ReaderPayment
                  amount={amount}
                  invoiceId={invoiceId}
                  onSuccess={handleSuccess}
                  onError={handleError}
                  onCancel={handleCancel}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
